import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import supportApiService from '../../services/supportApiService';

const SupportTokenRecharge = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [agents, setAgents] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [filteredAgents, setFilteredAgents] = useState([]);

  useEffect(() => {
    loadTokenRechargeData();
  }, []);

  useEffect(() => {
    // Filter agents based on search term
    if (searchTerm.trim() === '') {
      setFilteredAgents(agents);
    } else {
      const filtered = agents.filter(agent => 
        agent.agentId?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        agent.firstName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        agent.lastName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        agent.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        agent.phoneNumber?.includes(searchTerm)
      );
      setFilteredAgents(filtered);
    }
  }, [searchTerm, agents]);

  const loadTokenRechargeData = async () => {
    try {
      setLoading(true);
      console.log('🔍 Loading token recharge data...');
      const data = await supportApiService.getTokenRecharge();
      console.log('📊 Token Recharge API Response:', data);
      
      if (data.success) {
        setAgents(data.listOfAgent || []);
        setFilteredAgents(data.listOfAgent || []);
        console.log('✅ Token recharge data loaded successfully:', data.listOfAgent?.length, 'agents');
      } else {
        console.error('❌ Token Recharge API returned error:', data.message);
        toast.error(data.message || 'Failed to load token recharge data');
      }
    } catch (error) {
      console.error('❌ Token recharge load error:', error);
      toast.error('Failed to load token recharge data');
    } finally {
      setLoading(false);
    }
  };

  const handleRechargeClick = (agentId) => {
    navigate(`/support/dashboard/token-recharge-page/recharge/${agentId}`);
  };

  const handleHistoryClick = (agentId) => {
    navigate(`/support/dashboard/token-recharge/token-recharge-history/${agentId}`);
  };

  const formatName = (firstName, lastName) => {
    const first = firstName || '';
    const last = lastName || '';
    return `${first} ${last}`.trim() || '-';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="container mx-auto px-4 py-6 max-w-7xl">
        {/* Page Header */}
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-gray-900">Token Recharge</h1>
          <p className="text-gray-600">Manage agent token recharges and view token balances</p>
        </div>

        {/* Content Card */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200">
          {/* Card Header */}
          <div className="px-6 py-4 border-b border-gray-200">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-semibold text-gray-900">Tokens Details</h3>
              <div className="text-sm text-gray-500">
                Total: {filteredAgents.length} agents
              </div>
            </div>
          </div>

          {/* Search Bar */}
          <div className="px-6 py-4 border-b border-gray-200">
            <div className="max-w-md">
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <i className="fas fa-search text-gray-400"></i>
                </div>
                <input
                  type="text"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:ring-1 focus:ring-green-500 focus:border-green-500"
                  placeholder="Search by agent ID, name, email..."
                />
              </div>
            </div>
          </div>

          {/* Table */}
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Agent ID
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Agent Email
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Name
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Mobile Number
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Token
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Recharge
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredAgents.length > 0 ? (
                  filteredAgents.map((agent, index) => (
                    <tr key={agent.agentId || index} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {agent.agentId ? (
                          <button
                            onClick={() => handleHistoryClick(agent.agentId)}
                            className="text-blue-600 hover:text-blue-800 hover:underline font-medium"
                          >
                            {agent.agentId}
                          </button>
                        ) : (
                          '-'
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {agent.email || 'NA'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {formatName(agent.firstName, agent.lastName)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {agent.phoneNumber || '-'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                          {agent.tokens || 0} Tokens
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                        <button
                          onClick={() => handleRechargeClick(agent.agentId)}
                          className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-md text-sm font-medium transition-colors"
                        >
                          <i className="fas fa-dollar-sign mr-2"></i>
                          Recharge
                        </button>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="6" className="px-6 py-8 text-center text-gray-500">
                      {searchTerm ? 'No agents found matching your search.' : 'No agents found.'}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {/* Footer */}
          {filteredAgents.length > 0 && (
            <div className="px-6 py-4 border-t border-gray-200 bg-gray-50">
              <div className="text-sm text-gray-700">
                Showing {filteredAgents.length} of {agents.length} agents
                {searchTerm && (
                  <span className="ml-2">
                    (filtered by "{searchTerm}")
                  </span>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default SupportTokenRecharge;